import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import PageHeader from 'components/project/page_header';
import Button from 'shared/button';
import { put } from 'helpers/api';
import { fetchShareableTokens } from 'components/project/api';

import { faEye, faPaperPlane, faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Col } from 'react-bootstrap-5';

import { fetchLeads } from '../project/actions/index';
import { type Props as CompanyCardProps } from './company_card';
import { type Props as SyndicationProps } from './syndication_card';
import { type Props as LeadsProps } from './leads_card';
import { type Props as WebsiteTrafficProps } from './website_traffic';
import { type Props as ReferralProps } from './referral_source_card';
import { type EmailAnalyticsProps } from './email_analytics_card';
import { type ActivitySummaryProps } from './activity_summary_card';
import type { CustomCards, CustomCardProps } from './custom_card';
import {
  ActivityFunnelWithPreview,
  ActivitySummaryCardWithPreview,
  BasicCardWithPreview,
  CompanyCardWithPreview,
  CustomCardWithPreview,
  DocumentStatsCardWithPreview,
  EmailAnalyticsWithPreview,
  EmailDetailsWithPreview,
  LeadsCardWithPreview,
  ReferralSourceCardWithPreview,
  SyndicationCardWithPreview,
  WebsiteTrafficWithPreview,
  type Card as CardProps,
  initialDefaultCards,
  type ShareableToken,
  type Collaborator,
  useWindowSize
} from './util';
import ShareClientReportModal from './share_client_report_modal';
import LoadingCard from './loading_card';
import CustomCardModal from './custom_card_modal';
import type { Project } from 'bundles/client_reports/client_report_types';
import ClientReportFooter from '../client_reports/client_report_customization_banner';
import DatePicker from 'react-datepicker';

import './styles.scss';

type Metrics = CompanyCardProps &
  CustomCards &
  SyndicationProps &
  LeadsProps &
  ReferralProps &
  EmailAnalyticsProps &
  WebsiteTrafficProps &
  ActivitySummaryProps & {
    casSigned: number;
    casViewed: number;
    daysOnMarket: number;
    totalLeads: number;
    documentDownloads: number;
    documentViews: number;
    totalPublicDocViews: number;
    uniqueLockedViews: number;
    documentDownloadsByMonth: Record<string, number>;
    documentViewsByMonth: Record<string, number>;
  };

type Props = {
  collaborators?: Collaborator[];
  defaultCards: CardProps[];
  downloadPdfEnabled?: boolean;
  initialEndDate?: string;
  fetchFastMetrics: (startDate: string, endDate: string) => Promise<Metrics>;
  fetchAnalyticsMetrics: (startDate: string, endDate: string) => Promise<Metrics>;
  preview: boolean;
  project: Project,
  projectId: number;
  initialStartDate?: string;
}

export const GRAPH_HEIGHT = 250;
const MAX_NUM_COLUMNS = 12;

function setupCardsData(defaultCards?: CardProps[]): CardProps[] {
  return defaultCards?.length ? defaultCards : initialDefaultCards;
}

function normalizeDate(value: string | undefined | null): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;

  // Match plain date YYYY-MM-DD (no time component)
  const plainDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (plainDateMatch) {
    const [, yStr, mStr, dStr] = plainDateMatch;
    const y = Number(yStr);
    const m = Number(mStr); // 1–12
    const d = Number(dStr);

    // Create in local timezone at noon (reduces DST boundary surprises)
    const localDate = new Date(y, m - 1, d, 12, 0, 0, 0);

    // Validate it “round-trips” (invalid dates like 2025-02-30 will roll over)
    if (
      localDate.getFullYear() !== y ||
      localDate.getMonth() !== m - 1 ||
      localDate.getDate() !== d
    ) {
      return null;
    }
    return localDate;
  }

  // Otherwise let native parser handle full ISO or other formats with offsets
  const parsed = new Date(trimmed);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function UpdatedClientReport({
  collaborators = [],
  defaultCards = [],
  downloadPdfEnabled = false,
  fetchAnalyticsMetrics,
  fetchFastMetrics,
  initialEndDate,
  initialStartDate,
  preview = false,
  project,
  projectId
}: Props) {
  const { width } = useWindowSize();

  const RESPONSIVE_BREAKPOINT = 1300;
  const isSmallScreen = width ? width < RESPONSIVE_BREAKPOINT : false;

  const [metrics, setMetrics] = useState<Metrics>();
  const [cards, setCards] = useState<CardProps[]>(setupCardsData(defaultCards));
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareableTokens, setShareableTokens] = useState<ShareableToken[]>([]);
  const [customCardModalOpen, setCustomCardModalOpen] = useState(false);
  const [currentCardId, setCurrentCardId] = useState<number | undefined>(undefined);
  const [currentTitle, setCurrentTitle] = useState<string>('');
  const [currentSubtitle, setCurrentSubtitle] = useState<string>('');
  const [currentBody, setCurrentBody] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(normalizeDate(initialStartDate));
  const [endDate, setEndDate] = useState<Date | null>(normalizeDate(initialEndDate));

  const dispatch = preview ? null : useDispatch();

  useEffect(() => {
    if (preview || !dispatch) return;

    dispatch(fetchLeads(projectId));
  }, [dispatch, projectId]);

  const refreshShareableTokens = () => {
    fetchShareableTokens(projectId)
      .then((response) => {
        setShareableTokens(response.shareableTokens || []);
      });
  };

  const formatDate = (date: Date | null) => {
    return date === null ? '' : date.toLocaleDateString();
  };

  useEffect(() => {
    fetchFastMetrics(formatDate(startDate), formatDate(endDate)).then((fastMetrics) => {
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        ...fastMetrics
      }));
    });
    refreshShareableTokens();
  }, [customCardModalOpen]);

  useEffect(() => {
    fetchFastMetrics(formatDate(startDate), formatDate(endDate)).then((fastMetrics) => {
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        ...fastMetrics
      }));
    });
    fetchAnalyticsMetrics(formatDate(startDate), formatDate(endDate)).then((analyticsMetrics) => {
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        ...analyticsMetrics
      }));
    });
    refreshShareableTokens();
  }, [startDate, endDate]);

  const getCardFilters = (cardType: string) => {
    const card = cards.find(c => c.cardType === cardType);
    return card?.filters || {};
  };

  const getCardDisplayValue = (cardType: string): boolean => {
    const card = cards.find(c => c.cardType === cardType);
    return card?.displayCard !== false;
  };

  const getCardDisplayValueById = (cardId: number): boolean => {
    const card = cards.find(c => c.cardId === cardId);
    return card?.displayCard !== false;
  };

  const threeColWidth = fullSize => isSmallScreen ? 12 : fullSize;
  const twoColWidth = fullSize => isSmallScreen ? 12 : fullSize;

  const getVisibleCardsInRow = (rowCards: string[]) => {
    return rowCards.filter(card => getCardDisplayValue(card));
  };

  const getColWidth = (rowCards: string[]) => {
    const visibleCount = preview
      ? getVisibleCardsInRow(rowCards).length
      : rowCards.length;

    if (visibleCount === 0) return 0;
    if (visibleCount === 1) return 12;
    if (visibleCount === 2) return 6;
    return 4;
  };

  const basicMetricsRow = ['totalDaysOnMarket', 'totalCasSigned', 'totalLeads'];
  const basicColWidth = getColWidth(basicMetricsRow);

  const activityRow = ['activitySummary', 'activityFunnel', 'companyCaSummary'];
  const activityColWidth = getColWidth(activityRow);

  const getEmailAnalyticsColWidth = () => {
    if (!preview) return 4;

    const emailAnalyticsVisible = getCardDisplayValue('emailAnalytics');
    const emailDetailVisible = getCardDisplayValue('emailDetail');

    if (!emailAnalyticsVisible) return 0;
    return emailDetailVisible ? 4 : 12;
  };

  const websiteAndDocStatsRow = ['websiteTraffic', 'documentStats'];
  const websiteAndDocStatsColWidth = getColWidth(websiteAndDocStatsRow);

  const referralRow = ['referralSources', 'syndication'];
  const referralColWidth = getColWidth(referralRow);


  const handleCardUpdate = (
    cardType: string,
    updates: { filters?: any, displayCard?: boolean },
    cardId?: number
  ) => {
    const updatedCards = cards.map((card) => {
      const isMatch = cardId ? card.cardId === cardId : card.cardType === cardType;
      if (isMatch) {
        return {
          ...card,
          ...(updates.displayCard !== undefined ? { displayCard: updates.displayCard } : {}),
          ...(updates.filters ? {
            filters: {
              ...card.filters,
              ...updates.filters
            }
          } : {})
        };
      }
      return card;
    });

    setCards(updatedCards);
    saveCardData(updatedCards);
  };

  const saveCardData = async (cards: CardProps[]): Promise<any> => {
    return put(`/projects/${projectId}/client_report`, { cards });
  };

  const handlePreviewClick = () => {
    const baseUrl = `/projects/${projectId}/client_report/preview`;
    window.open(baseUrl, '_blank');
  };

  // TODO: change toggle behavior to just show
  const toggleCustomCardModal = () => {
    setCustomCardModalOpen(!customCardModalOpen);
  };

  const addCustomCard = () => {
    setCurrentCardId(undefined);
    setCurrentTitle('');
    setCurrentSubtitle('');
    setCurrentBody('');
    toggleCustomCardModal();
  };

  const editCustomCard = (card: CustomCardProps) => {
    setCurrentCardId(card.id);
    setCurrentTitle(card.title || '');
    setCurrentSubtitle(card.subtitle || '');
    setCurrentBody(card.body || '');
    toggleCustomCardModal();
  };

  const pageHeaderActions = (
    <PageHeader.Actions>
      <div className="d-flex align-items-center gap-3" style={{ paddingBottom: '15px' }}>
        <Button.Primary icon={faPlus} plain onClick={addCustomCard}>
          Add Custom Card
        </Button.Primary>
        <Button.Accent icon={faEye} outlined onClick={handlePreviewClick} >
          Preview
        </Button.Accent>
        <Button.Primary icon={faPaperPlane} onClick={() => setShowShareModal(true)}>Share Summary</Button.Primary>
      </div>
    </PageHeader.Actions>
  );

  const clearDateFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const dateSelector = (
    <div className="datepicker-container">
      <div>
        <DatePicker
          className="client-report-date-picker"
          endDate={endDate}
          maxDate={endDate || undefined}
          placeholderText="Start Date"
          selected={startDate}
          selectsStart
          showMonthDropdown
          showYearDropdown
          startDate={startDate}
          onChange={(date: Date | null) => setStartDate(date)}
        />
      </div>
      <div>
        <DatePicker
          className="client-report-date-picker"
          endDate={endDate}
          minDate={startDate || undefined}
          placeholderText="End Date"
          selected={endDate}
          selectsEnd
          showMonthDropdown
          showYearDropdown
          startDate={startDate}
          onChange={(date: Date | null) => setEndDate(date)}
        />
      </div>
      {(startDate || endDate) &&
        <Button.Primary icon={faXmark} plain onClick={clearDateFilters}>Clear All</Button.Primary>
      }
    </div>
  );

  const renderDaysOnMarket = () => {
    const metricsAvailable = metrics !== undefined && metrics.daysOnMarket !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('totalDaysOnMarket');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4" md={threeColWidth(basicColWidth)}>
          <BasicCardWithPreview
            displayCard={getCardDisplayValue('totalDaysOnMarket')}
            preview={preview}
            text="Total Days on Market"
            value={metrics?.daysOnMarket}
            onPreviewChange={isHidden => handleCardUpdate('totalDaysOnMarket', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderSignedCas = () => {
    const metricsAvailable = metrics !== undefined && metrics.casSigned !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('totalCasSigned');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4" md={threeColWidth(basicColWidth)}>
          <BasicCardWithPreview
            displayCard={getCardDisplayValue('totalCasSigned')}
            preview={preview}
            text="CA's Signed"
            value={metrics.casSigned}
            onPreviewChange={isHidden => handleCardUpdate('totalCasSigned', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderTotalLeads = () => {
    const metricsAvailable = metrics !== undefined && metrics.totalLeads !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('totalLeads');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4" md={threeColWidth(basicColWidth)}>
          <BasicCardWithPreview
            displayCard={getCardDisplayValue('totalLeads')}
            preview={preview}
            text="Leads"
            value={metrics.totalLeads}
            onPreviewChange={isHidden => handleCardUpdate('totalLeads', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderActivitySummary = () => {
    const metricsAvailable = metrics !== undefined && metrics.activitySummary !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('activitySummary');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={threeColWidth(activityColWidth)}>
          <ActivitySummaryCardWithPreview
            activitySummary={metrics.activitySummary}
            displayCard={getCardDisplayValue('activitySummary')}
            preview={preview}
            projectId={projectId}
            onPreviewChange={isHidden => handleCardUpdate('activitySummary', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderActivityFunnel = () => {
    const metricsAvailable = metrics !== undefined
                             && metrics.casSigned !== undefined
                             && metrics.casViewed !== undefined
                             && metrics.documentDownloads !== undefined
                             && metrics.documentViews !== undefined
                             && metrics.emailAnalytics !== undefined
                             && metrics.websiteViews !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('activityFunnel');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={threeColWidth(activityColWidth)}>
          <ActivityFunnelWithPreview
            casSigned={metrics.casSigned}
            casViewed={metrics.casViewed}
            displayCard={getCardDisplayValue('activityFunnel')}
            documentDownloads={metrics.documentDownloads}
            documentViews={metrics.documentViews}
            emailAnalytics={metrics.emailAnalytics}
            preview={preview}
            totalPublicDocViews={metrics.totalPublicDocViews}
            uniqueLockedViews={metrics.uniqueLockedViews}
            websiteViews={metrics.websiteViews}
            onPreviewChange={isHidden => handleCardUpdate('activityFunnel', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderCompaniesCard = () => {
    const metricsAvailable = metrics !== undefined && metrics.leadCountByCompanyName !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('companyCaSummary');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={threeColWidth(activityColWidth)}>
          <CompanyCardWithPreview
            displayCard={getCardDisplayValue('companyCaSummary')}
            leadCountByCompanyName={metrics.leadCountByCompanyName}
            preview={preview}
            onPreviewChange={isHidden => handleCardUpdate('companyCaSummary', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };


  const renderDocumentStats = () => {
    const metricsAvailable =
      metrics !== undefined &&
      metrics.documentDownloadsByMonth !== undefined &&
      metrics.documentViewsByMonth !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('documentStats');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={twoColWidth(websiteAndDocStatsColWidth)}>
          <DocumentStatsCardWithPreview
            displayCard={getCardDisplayValue('documentStats')}
            documentDownloadsByMonth={metrics.documentDownloadsByMonth}
            documentViewsByMonth={metrics.documentViewsByMonth}
            preview={preview}
            onPreviewChange={isHidden => handleCardUpdate('documentStats', { displayCard: !isHidden })}
          />
        </Col>
      );
    }
    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };
  
  const renderLeads = () => {
    const metricsAvailable = metrics !== undefined && metrics.leads !== undefined && metrics.leadFilters !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('leads');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 px-0" md={12}>
          <LeadsCardWithPreview
            displayCard={getCardDisplayValue('leads')}
            filters={getCardFilters('leads')}
            leadFilters={metrics.leadFilters}
            leads={metrics.leads}
            preview={preview}
            projectId={projectId}
            onFilterUpdate={filters => handleCardUpdate('leads', { filters })}
            onPreviewChange={isHidden => handleCardUpdate('leads', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderEmailAnalytics = () => {
    const metricsAvailable = metrics !== undefined && metrics.emailAnalytics !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('emailAnalytics');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={getEmailAnalyticsColWidth()}>
          <EmailAnalyticsWithPreview
            displayCard={getCardDisplayValue('emailAnalytics')}
            emailAnalytics={metrics.emailAnalytics}
            filters={getCardFilters('emailAnalytics')}
            preview={preview}
            onFilterUpdate={filters => handleCardUpdate('emailAnalytics', { filters })}
            onPreviewChange={isHidden => handleCardUpdate('emailAnalytics', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderEmailDetail = () => {
    const metricsAvailable = metrics !== undefined && metrics.emailAnalytics !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('emailDetail');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={MAX_NUM_COLUMNS - getEmailAnalyticsColWidth()}>
          <EmailDetailsWithPreview
            displayCard={getCardDisplayValue('emailDetail')}
            emailAnalytics={metrics.emailAnalytics}
            preview={preview}
            onPreviewChange={isHidden => handleCardUpdate('emailDetail', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderWebsiteTraffic = () => {
    const metricsAvailable = metrics !== undefined
                             && metrics.timeOnPage !== undefined
                             && metrics.uniqueVisitors !== undefined
                             && metrics.websiteViews !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('websiteTraffic');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={twoColWidth(websiteAndDocStatsColWidth)}>
          <WebsiteTrafficWithPreview
            displayCard={getCardDisplayValue('websiteTraffic')}
            filters={getCardFilters('websiteTraffic')}
            preview={preview}
            timeOnPage={metrics.timeOnPage}
            uniqueVisitors={metrics.uniqueVisitors}
            websiteViews={metrics.websiteViews}
            onFilterUpdate={filters => handleCardUpdate('websiteTraffic', { filters })}
            onPreviewChange={isHidden => handleCardUpdate('websiteTraffic', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderReferralSourceCard = () => {
    const metricsAvailable = metrics !== undefined && metrics.referralCountBySource !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('referralSources');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={twoColWidth(referralColWidth)}>
          <ReferralSourceCardWithPreview
            displayCard={getCardDisplayValue('referralSources')}
            preview={preview}
            referralCountBySource={metrics.referralCountBySource}
            onPreviewChange={isHidden => handleCardUpdate('referralSources', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderSyndicationPartners = () => {
    const metricsAvailable = metrics !== undefined && metrics.syndicationPartners !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('syndication');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={(twoColWidth(referralColWidth))}>
          <SyndicationCardWithPreview
            displayCard={getCardDisplayValue('syndication')}
            preview={preview}
            syndicationPartners={metrics.syndicationPartners}
            onPreviewChange={isHidden => handleCardUpdate('syndication', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const customColWidth = (cardCount: number) => {
    if (isSmallScreen) return 12;
    if (!preview) return 4;
    if (cardCount === 1) return 12;
    if (cardCount === 2) return 6;
    return 4;
  };

  const customCardRow = (cards: CustomCardProps[]) => {
    const displayedCards = cards.filter(card => getCardDisplayValueById(card.id) === true || !preview);
    const displayedCardsCount = displayedCards.length;

    return (
      <div className="row align-items-start">
        {displayedCards.map(card => (
          <Col className="mb-4" key={card.id} md={customColWidth(displayedCardsCount)}>
            <CustomCardWithPreview
              key={card.id}
              {...card}
              displayCard={getCardDisplayValueById(card.id)}
              editCard={() => editCustomCard(card)}
              preview={preview}
              onPreviewChange={isHidden => handleCardUpdate('custom', { displayCard: !isHidden }, card.id)}
            />
          </Col>
        ))}
      </div>
    );
  };

  const renderCustomCards = () => {
    const { customCards } = metrics || {};
    if (!customCards || customCards.length === 0) return null;

    if (isSmallScreen) {
      return customCards.map(card => customCardRow([card]));
    }

    // Split customCards into groups of three and render each row
    const rows: React.JSX.Element[] = [];
    for (let i = 0; i < customCards.length; i += 3) {
      rows.push(customCardRow(customCards.slice(i, i + 3)));
    }
    return <>{rows}</>;
  };

  return (
    <React.Fragment>
      <ShareClientReportModal
        collaborators={collaborators}
        downloadPdfEnabled={downloadPdfEnabled}
        projectId={projectId}
        shareableTokens={shareableTokens}
        show={showShareModal}
        showAddEmail={project.canShareClientReport}
        onCloseModal={() => setShowShareModal(false)}
        onTokensChanged={refreshShareableTokens}
      />
      {customCardModalOpen && (
        <CustomCardModal
          cardId={currentCardId}
          currentBody={currentBody}
          currentSubtitle={currentSubtitle}
          currentTitle={currentTitle}
          modalOpen={customCardModalOpen}
          projectId={projectId}
          onCloseModal={() => setCustomCardModalOpen(false)}
        />
      )}
      {!preview &&
      <PageHeader
        actions={pageHeaderActions}
        belowTitleAction={dateSelector}
        title="Client Report" useNewDesign
      />}
      {
        // Remove the styling in the div below when we remove the customization banner
      }
      <div className="d-flex flex-column gap-4" style={{ paddingBottom: '40px' }}>
        <div className="row">
          {renderDaysOnMarket()}
          {renderSignedCas()}
          {renderTotalLeads()}
        </div>

        <div className="row">
          {renderActivitySummary()}
          {renderActivityFunnel()}
          {renderCompaniesCard()}
        </div>

        <div className="row" style={{ marginRight: '0', marginLeft: '0' }}>
          {renderLeads()}
        </div>

        <div className="row">
          {renderEmailAnalytics()}
          {renderEmailDetail()}
        </div>

        <div className="row">
          {renderWebsiteTraffic()}
          {renderDocumentStats()}
        </div>

        <div className="row">
          {renderReferralSourceCard()}
          {renderSyndicationPartners()}
        </div>

        {renderCustomCards()}
        { !preview && <ClientReportFooter project={project} /> }
      </div>
    </React.Fragment>
  );
}

const mapStateToProps = (state: any) => ({
  leads: state.leads.fetching ? [] : state.leads,
  projectId: state.project.id,
});

const mapDispatchToProps = {
  fetchLeads,
};

export { UpdatedClientReport };
export default connect(mapStateToProps, mapDispatchToProps)(UpdatedClientReport);
